import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { apiClient } from '@/api/client'
import { MapPin, User, Phone, Car, Luggage, FileText, CheckCircle, AlertCircle, Calendar, Clock, ClipboardList, PlusCircle, ChevronDown, ChevronUp, Plus, X, Tag, ReceiptText, ArrowRight } from 'lucide-react'
import { SearchableSelect } from '@/components/ui/SearchableSelect'
import { MonthDayPicker } from '@/components/ui/month-day-picker'
import { TimePicker } from '@/components/ui/time-picker'
import AddressAutocomplete from '@/components/address/AddressAutocomplete'
import ContactAutocomplete, { type Contact } from '@/components/booking/ContactAutocomplete'
import { format } from 'date-fns'
import { zhTW } from 'date-fns/locale'

interface PriceRule {
  id: string
  from_area: string
  to_area: string
  price: number
  vehicle_type?: string
  is_bidirectional: boolean
  note?: string
}

interface AdditionalStop {
  address: string
  order: number
  name?: string
  phone?: string
}

interface CustomerOrder {
  order_number: string
  order_category: string
  status: string
  pickup_datetime: string
  pickup_location: string
  dropoff_location: string
  passenger_name: string
  passenger_phone?: string
  passenger_count?: number
  flight_number?: string
  driver_notes?: string
  vehicle_type: string
  driver_name?: string
  driver_phone?: string
  vehicle_number?: string
  edited_by_customer?: boolean
  additional_stops?: AdditionalStop[]
}

interface BookingInfo {
  customer_name: string
  contact_person?: string
  contact_phone?: string
  tenant_name: string
  vehicle_categories: string[]
  payment_methods: string[]
}

const ORDER_CATEGORIES = ['交通趟', '送機', '接機']

const AIRPORTS = [
  '桃園機場一航廈',
  '桃園機場二航廈',
  '松山機場',
  '台中機場',
  '高雄機場',
  '基隆港',
]

const getTodayDate = () => {
  const today = new Date()
  return `${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`
}

const formatToISO8601 = (dateStr: string, timeStr: string) => {
  const currentYear = new Date().getFullYear()
  const [month, day] = dateStr.split('-')
  const [hours, minutes] = timeStr.split(':')
  return `${currentYear}-${month}-${day}T${hours}:${minutes}:00`
}

const inputCls = (hasError?: boolean) =>
  `w-full border rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-300 bg-white ${hasError ? 'border-red-400' : 'border-gray-300'}`

export default function BookingPage() {
  const { token } = useParams<{ token: string }>()
  const [info, setInfo] = useState<BookingInfo | null>(null)
  const [loadError, setLoadError] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [orderNumber, setOrderNumber] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [tab, setTab] = useState<'booking' | 'orders' | 'prices' | 'contacts'>('booking')
  const [orders, setOrders] = useState<CustomerOrder[]>([])
  const [ordersLoading, setOrdersLoading] = useState(false)
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null)
  const [extraStops, setExtraStops] = useState<{ address: string; name: string; phone: string }[]>([])
  const [toast, setToast] = useState<string | null>(null)
  const [contactsList, setContactsList] = useState<Contact[]>([])
  const [contactsLoading, setContactsLoading] = useState(false)
  const [editingOrder, setEditingOrder] = useState<string | null>(null)
  const [editForm, setEditForm] = useState({
    pickup_date: '', pickup_time: '', pickup_location: '', dropoff_location: '',
    passenger_name: '', passenger_phone: '', passenger_count: 1,
    flight_number: '', driver_notes: '',
  })
  const [editExtraStops, setEditExtraStops] = useState<{ address: string; name: string; phone: string }[]>([])
  const [editSubmitting, setEditSubmitting] = useState(false)
  const [editError, setEditError] = useState('')
  const [priceRules, setPriceRules] = useState<PriceRule[]>([])
  const [fromArea, setFromArea] = useState('')
  const [toArea, setToArea] = useState('')

  // PIN 驗證
  const STORAGE_KEY = `booking_verified_${token}`
  const [pinVerified, setPinVerified] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (!stored) return false
      const { pin, expiry } = JSON.parse(stored)
      return expiry > Date.now() ? pin : false
    } catch { return false }
  })
  const [pinInput, setPinInput] = useState('')
  const [pinError, setPinError] = useState('')
  const [pinLoading, setPinLoading] = useState(false)

  const [form, setForm] = useState({
    order_category: '交通趟',
    passenger_name: '',
    passenger_phone: '',
    passenger_count: 1,
    pickup_date: getTodayDate(),
    pickup_time: '',
    pickup_location: '',
    dropoff_location: '',
    vehicle_type: '',
    luggage_large: 0,
    luggage_small: 0,
    flight_number: '',
    flight_time: '',
    driver_notes: '',
  })

  useEffect(() => {
    if (!token) return
    apiClient.get(`/public/booking/${token}`)
      .then(res => {
        setInfo(res.data)
        if (res.data.vehicle_categories?.length > 0) {
          setForm(f => ({ ...f, vehicle_type: res.data.vehicle_categories[0] }))
        }
      })
      .catch(() => setLoadError('此連結無效或已失效，請聯絡服務人員'))
    apiClient.get(`/public/booking/${token}/price-rules`)
      .then(res => setPriceRules(res.data))
      .catch(() => {})
  }, [token])

  // Derive matched contract price
  const matchedRule = priceRules.find(r => {
    if (r.vehicle_type && form.vehicle_type && r.vehicle_type !== form.vehicle_type) return false
    if (r.from_area === fromArea && r.to_area === toArea) return true
    if (r.is_bidirectional && r.from_area === toArea && r.to_area === fromArea) return true
    return false
  }) || null

  const uniqueAreas = Array.from(new Set([
    ...priceRules.map(r => r.from_area),
    ...priceRules.map(r => r.to_area),
  ])).sort()

  const fetchOrders = useCallback((pin: string) => {
    if (!token) return
    setOrdersLoading(true)
    apiClient.get(`/public/booking/${token}/orders`, { headers: { 'x-booking-pin': pin } })
      .then(res => setOrders(res.data.orders))
      .catch(() => {})
      .finally(() => setOrdersLoading(false))
  }, [token])

  const fetchContactsList = useCallback((pin: string) => {
    if (!token) return
    setContactsLoading(true)
    apiClient.get(`/public/booking/${token}/contacts/list`, { headers: { 'x-booking-pin': pin } })
      .then(res => setContactsList(res.data || []))
      .catch(() => {})
      .finally(() => setContactsLoading(false))
  }, [token])

  const handleDeleteContact = async (contact: Contact) => {
    if (!token || !pinVerified) return
    try {
      await apiClient.delete(`/public/booking/${token}/contacts/${contact.id}`, { headers: { 'x-booking-pin': pinVerified } })
      setContactsList(prev => prev.filter(c => c.id !== contact.id))
      showToast(`已刪除「${contact.name}」`)
    } catch {
      showToast('刪除失敗，請稍後再試')
    }
  }

  const startEditOrder = (order: CustomerOrder) => {
    const dt = new Date(order.pickup_datetime)
    setEditForm({
      pickup_date: `${(dt.getMonth() + 1).toString().padStart(2, '0')}-${dt.getDate().toString().padStart(2, '0')}`,
      pickup_time: `${dt.getHours().toString().padStart(2, '0')}:${dt.getMinutes().toString().padStart(2, '0')}`,
      pickup_location: order.pickup_location,
      dropoff_location: order.dropoff_location,
      passenger_name: order.passenger_name,
      passenger_phone: order.passenger_phone || '',
      passenger_count: order.passenger_count || 1,
      flight_number: order.flight_number || '',
      driver_notes: order.driver_notes || '',
    })
    setEditExtraStops(
      (order.additional_stops || [])
        .slice()
        .sort((a, b) => a.order - b.order)
        .map(s => ({ address: s.address, name: s.name || '', phone: s.phone || '' }))
    )
    setEditError('')
    setEditingOrder(order.order_number)
  }

  const submitEditOrder = async (orderNumber: string) => {
    if (!token || !pinVerified) return
    setEditSubmitting(true)
    setEditError('')
    try {
      await apiClient.patch(`/public/booking/${token}/orders/${orderNumber}`, {
        pickup_datetime: formatToISO8601(editForm.pickup_date, editForm.pickup_time),
        pickup_location: editForm.pickup_location,
        dropoff_location: editForm.dropoff_location,
        passenger_name: editForm.passenger_name,
        passenger_phone: editForm.passenger_phone,
        passenger_count: Number(editForm.passenger_count),
        flight_number: editForm.flight_number || undefined,
        driver_notes: editForm.driver_notes || undefined,
        additional_stops: editExtraStops.filter(s => s.address.trim()).map((s, i) => ({
          address: s.address, order: i + 1,
          name: s.name.trim() || undefined,
          phone: s.phone.trim() || undefined,
        })),
      }, { headers: { 'x-booking-pin': pinVerified } })
      setEditingOrder(null)
      fetchOrders(pinVerified)
    } catch (e: any) {
      setEditError(e?.response?.data?.detail || '修改失敗，請稍後再試')
    } finally {
      setEditSubmitting(false)
    }
  }

  useEffect(() => {
    if (tab === 'orders' && pinVerified) fetchOrders(pinVerified)
    if (tab === 'contacts' && pinVerified) fetchContactsList(pinVerified)
  }, [tab, pinVerified, fetchOrders, fetchContactsList])

  const handleVerifyPin = async () => {
    if (!pinInput.trim()) { setPinError('請輸入驗證碼'); return }
    setPinLoading(true)
    setPinError('')
    try {
      await apiClient.post(`/public/booking/${token}/verify`, { pin: pinInput.trim() })
      const expiry = Date.now() + 24 * 60 * 60 * 1000
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ pin: pinInput.trim(), expiry }))
      setPinVerified(pinInput.trim())
    } catch (e: any) {
      setPinError(e?.response?.data?.detail || '驗證碼錯誤')
    } finally {
      setPinLoading(false)
    }
  }

  const showToast = (message: string) => {
    setToast(message)
    setTimeout(() => setToast(null), 2000)
  }

  const saveContact = async (name: string, phone: string, address?: string) => {
    if (!token || !name.trim() || !phone.trim()) return
    try {
      await apiClient.post(`/public/booking/${token}/contacts`, {
        name: name.trim(),
        phone: phone.trim(),
        address: address?.trim() || undefined,
      })
      showToast(`已將「${name.trim()}」加入常用聯絡人`)
    } catch {
      showToast('加入失敗，請稍後再試')
    }
  }

  const set = (field: string, value: string | number) =>
    setForm(f => ({ ...f, [field]: value }))

  const validate = () => {
    const e: Record<string, string> = {}
    if (!form.passenger_name.trim()) e.passenger_name = '請填寫乘客姓名'
    if (!form.passenger_phone.trim()) e.passenger_phone = '請填寫聯絡電話'
    if (!form.pickup_date) e.pickup_date = '請選擇日期'
    if (!form.pickup_time) e.pickup_time = '請選擇時間'
    if (!form.pickup_location.trim()) e.pickup_location = '請填寫上車地點'
    if (!form.dropoff_location.trim()) e.dropoff_location = '請填寫下車地點'
    if (!form.vehicle_type) e.vehicle_type = '請選擇車型'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setSubmitting(true)
    try {
      const isAirport = form.order_category === '送機' || form.order_category === '接機'
      const payload = {
        order_category: form.order_category === '交通趟' ? '其他' : form.order_category,
        passenger_name: form.passenger_name,
        passenger_phone: form.passenger_phone,
        passenger_count: Number(form.passenger_count),
        pickup_datetime: formatToISO8601(form.pickup_date, form.pickup_time),
        pickup_location: form.pickup_location,
        dropoff_location: form.dropoff_location,
        additional_stops: extraStops.filter(s => s.address.trim()).map((s, i) => ({
          address: s.address, order: i + 1,
          name: s.name.trim() || undefined,
          phone: s.phone.trim() || undefined,
        })),
        vehicle_type: form.vehicle_type,
        luggage_count: Number(form.luggage_large) + Number(form.luggage_small),
        driver_notes: [
          matchedRule ? `[合約價格] NT$${matchedRule.price.toLocaleString()} (${matchedRule.from_area}→${matchedRule.to_area})` : '',
          (Number(form.luggage_large) > 0 ? `大行李 ${form.luggage_large} 件` : ''),
          (Number(form.luggage_small) > 0 ? `小行李 ${form.luggage_small} 件` : ''),
          form.driver_notes,
        ].filter(Boolean).join('、') || undefined,
        flight_number: isAirport && form.flight_number ? form.flight_number : undefined,
        arrival_time: isAirport && form.flight_time ? form.flight_time : undefined,
        contract_price: matchedRule ? matchedRule.price : undefined,
      }
      const res = await apiClient.post(`/public/booking/${token}`, payload)
      setOrderNumber(res.data.order_number)
      setSubmitted(true)
      if (pinVerified) fetchOrders(pinVerified)
    } catch {
      setErrors({ submit: '提交失敗，請稍後再試' })
    } finally {
      setSubmitting(false)
    }
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#EFF6FF] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-sm">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-gray-600">{loadError}</p>
        </div>
      </div>
    )
  }

  if (!info) {
    return (
      <div className="min-h-screen bg-[#EFF6FF] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  const isAirport = form.order_category === '送機' || form.order_category === '接機'
  const pickupIsAirport = form.order_category === '接機'
  const dropoffIsAirport = form.order_category === '送機'

  const STATUS_STYLE: Record<string, string> = {
    '待派車': 'bg-pink-100 text-pink-700',
    '已派車': 'bg-blue-100 text-blue-700',
    '進行中': 'bg-yellow-100 text-yellow-700',
    '已完成': 'bg-green-100 text-green-700',
    '已取消': 'bg-gray-100 text-gray-500',
  }

  return (
    <div className="min-h-screen bg-[#EFF6FF] py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm px-5 py-4 mb-3 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs text-gray-400">{info.tenant_name}</p>
            <h1 className="text-lg font-bold text-[#1E40AF] truncate">{info.customer_name}</h1>
          </div>
          {(info.contact_person || info.contact_phone) && (
            <div className="flex flex-col items-end gap-1 shrink-0 text-sm">
              {info.contact_person && (
                <span className="flex items-center gap-1 text-gray-600">
                  <User className="w-3.5 h-3.5" />{info.contact_person}
                </span>
              )}
              {info.contact_phone && (
                <a href={`tel:${info.contact_phone}`} className="flex items-center gap-1 text-blue-600">
                  <Phone className="w-3.5 h-3.5" />{info.contact_phone}
                </a>
              )}
            </div>
          )}
        </div>

        {/* Tab */}
        <div className="flex bg-white rounded-xl shadow-sm mb-4 p-1">
          <button
            type="button"
            onClick={() => setTab('booking')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              tab === 'booking' ? 'bg-[#2563EB] text-white' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <PlusCircle className="w-4 h-4" />填寫預約
          </button>
          <button
            type="button"
            onClick={() => setTab('orders')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              tab === 'orders' ? 'bg-[#2563EB] text-white' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <ClipboardList className="w-4 h-4" />我的訂單
            {orders.length > 0 && (
              <span className="bg-blue-100 text-blue-700 text-xs rounded-full px-1.5 py-0.5">{orders.length}</span>
            )}
          </button>
          {priceRules.length > 0 && (
            <button
              type="button"
              onClick={() => setTab('prices')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                tab === 'prices' ? 'bg-[#2563EB] text-white' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <ReceiptText className="w-4 h-4" />價目表
            </button>
          )}
          <button
            type="button"
            onClick={() => setTab('contacts')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              tab === 'contacts' ? 'bg-[#2563EB] text-white' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <User className="w-4 h-4" />常用聯絡人
          </button>
        </div>

        {/* 訂單列表 */}
        {tab === 'orders' && !pinVerified && (
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center space-y-4">
            <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mx-auto">
              <Phone className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="font-medium text-gray-800 mb-1">請輸入驗證碼</p>
              <p className="text-xs text-gray-400">預設為您的手機末 4 碼，或管理員設定的驗證碼</p>
            </div>
            <div className="max-w-xs mx-auto space-y-3">
              <input
                type="tel"
                maxLength={10}
                value={pinInput}
                onChange={e => { setPinInput(e.target.value); setPinError('') }}
                onKeyDown={e => e.key === 'Enter' && handleVerifyPin()}
                placeholder="輸入驗證碼"
                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-center text-lg tracking-widest outline-none focus:ring-2 focus:ring-blue-300"
                autoFocus
              />
              {pinError && <p className="text-red-500 text-sm">{pinError}</p>}
              <button
                type="button"
                onClick={handleVerifyPin}
                disabled={pinLoading}
                className="w-full bg-[#2563EB] hover:bg-blue-700 text-white font-medium py-2.5 rounded-xl transition-colors disabled:opacity-60"
              >
                {pinLoading ? '驗證中...' : '確認驗證'}
              </button>
            </div>
          </div>
        )}

        {tab === 'orders' && pinVerified && (
          <div className="space-y-3">
            {ordersLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : orders.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm p-10 text-center text-gray-400">
                <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">尚無預約紀錄</p>
              </div>
            ) : orders.map(order => {
              const isExpanded = expandedOrder === order.order_number
              return (
                <div key={order.order_number} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setExpandedOrder(isExpanded ? null : order.order_number)}
                    className="w-full px-5 py-4 flex items-start justify-between text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLE[order.status] || 'bg-gray-100 text-gray-500'}`}>
                          {order.status}
                        </span>
                        <span className="text-xs text-gray-400 font-mono">#{order.order_number.slice(-8)}</span>
                        <span className="text-xs text-gray-400">{order.order_category}</span>
                        {order.edited_by_customer && (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">已修改</span>
                        )}
                      </div>
                      <div className="mt-1.5 text-sm font-medium text-gray-800">
                        {order.pickup_datetime
                          ? format(new Date(order.pickup_datetime), 'MM/dd HH:mm', { locale: zhTW })
                          : '—'}
                      </div>
                      <div className="mt-1 text-xs text-gray-500 truncate">
                        {order.pickup_location} → {order.dropoff_location}
                      </div>
                    </div>
                    {isExpanded
                      ? <ChevronUp className="w-4 h-4 text-gray-400 mt-1 shrink-0" />
                      : <ChevronDown className="w-4 h-4 text-gray-400 mt-1 shrink-0" />
                    }
                  </button>

                  {isExpanded && (
                    <div className="px-5 pb-4 border-t border-gray-100 pt-3 space-y-2 text-sm text-gray-600">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        <div><span className="text-gray-400 text-xs">乘客</span><p>{order.passenger_name}</p></div>
                        <div><span className="text-gray-400 text-xs">車型</span><p>{order.vehicle_type}</p></div>
                        <div className="col-span-2"><span className="text-gray-400 text-xs">上車地點</span><p>{order.pickup_location}</p></div>
                        <div className="col-span-2"><span className="text-gray-400 text-xs">下車地點</span><p>{order.dropoff_location}</p></div>
                      </div>
                      {(order.driver_name || order.vehicle_number) && (
                        <div className="mt-3 bg-blue-50 rounded-xl px-4 py-3 space-y-1">
                          <p className="text-xs font-medium text-blue-700 mb-1">司機資訊</p>
                          {order.driver_name && (
                            <div className="flex items-center gap-2">
                              <User className="w-3.5 h-3.5 text-blue-500" />
                              <span>{order.driver_name}</span>
                              {order.driver_phone && (
                                <a href={`tel:${order.driver_phone}`} className="text-blue-600 underline">{order.driver_phone}</a>
                              )}
                            </div>
                          )}
                          {order.vehicle_number && (
                            <div className="flex items-center gap-2">
                              <Car className="w-3.5 h-3.5 text-blue-500" />
                              <span>{order.vehicle_number}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {(order.status === '待派車' || order.status === '已派車') && editingOrder !== order.order_number && (
                        <button
                          type="button"
                          onClick={() => startEditOrder(order)}
                          className="mt-3 w-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium py-2 rounded-lg transition-colors"
                        >
                          修改此訂單
                        </button>
                      )}

                      {editingOrder === order.order_number && (
                        <div className="mt-3 border-t border-gray-100 pt-3 space-y-3">
                          {order.status === '已派車' && (
                            <p className="text-xs text-orange-600 bg-orange-50 rounded-lg px-3 py-2">
                              此訂單已指派司機，修改後將退回「待派車」，由司機重新安排。
                            </p>
                          )}
                          <div className="grid grid-cols-2 gap-3">
                            <MonthDayPicker value={editForm.pickup_date} onChange={v => setEditForm(f => ({ ...f, pickup_date: v }))} placeholder="日期" />
                            <TimePicker value={editForm.pickup_time} onChange={v => setEditForm(f => ({ ...f, pickup_time: v }))} placeholder="時間" />
                          </div>
                          <AddressAutocomplete placeholder="上車地點" value={editForm.pickup_location}
                            onChange={v => setEditForm(f => ({ ...f, pickup_location: v }))} required />
                          <AddressAutocomplete placeholder="下車地點" value={editForm.dropoff_location}
                            onChange={v => setEditForm(f => ({ ...f, dropoff_location: v }))} required />

                          {/* 附加乘客 */}
                          {editExtraStops.map((stop, idx) => (
                            <div key={idx} className="flex gap-2 items-start bg-gray-50 rounded-lg p-2">
                              <div className="flex-1 space-y-2">
                                <AddressAutocomplete placeholder="附加乘客地址" value={stop.address}
                                  onChange={v => {
                                    const updated = [...editExtraStops]
                                    updated[idx] = { ...updated[idx], address: v }
                                    setEditExtraStops(updated)
                                  }} />
                                <div className="grid grid-cols-2 gap-2">
                                  <ContactAutocomplete
                                    token={token || ''}
                pin={pinVerified || undefined}
                onDeleted={(c: Contact) => showToast(`已刪除「${c.name}」`)}
                                    value={stop.name}
                                    onChange={v => {
                                      const updated = [...editExtraStops]
                                      updated[idx] = { ...updated[idx], name: v }
                                      setEditExtraStops(updated)
                                    }}
                                    onSelect={(c: Contact) => {
                                      const updated = [...editExtraStops]
                                      updated[idx] = { ...updated[idx], name: c.name, phone: c.phone, address: c.address || updated[idx].address }
                                      setEditExtraStops(updated)
                                    }}
                                    placeholder="姓名（選填）"
                                  />
                                  <ContactAutocomplete
                                    token={token || ''}
                pin={pinVerified || undefined}
                onDeleted={(c: Contact) => showToast(`已刪除「${c.name}」`)}
                                    type="tel"
                                    value={stop.phone}
                                    onChange={v => {
                                      const updated = [...editExtraStops]
                                      updated[idx] = { ...updated[idx], phone: v }
                                      setEditExtraStops(updated)
                                    }}
                                    onSelect={(c: Contact) => {
                                      const updated = [...editExtraStops]
                                      updated[idx] = { ...updated[idx], name: c.name, phone: c.phone, address: c.address || updated[idx].address }
                                      setEditExtraStops(updated)
                                    }}
                                    placeholder="電話（選填）"
                                  />
                                </div>
                                {stop.name.trim() && stop.phone.trim() && (
                                  <button type="button"
                                    onClick={() => saveContact(stop.name, stop.phone, stop.address)}
                                    className="text-xs text-blue-600 hover:text-blue-700 underline">
                                    + 加入常用聯絡人
                                  </button>
                                )}
                              </div>
                              <div className="flex flex-col gap-1 mt-1 shrink-0">
                                <button type="button" disabled={idx === 0}
                                  onClick={() => {
                                    const updated = [...editExtraStops]
                                    ;[updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]]
                                    setEditExtraStops(updated)
                                  }}
                                  className="p-1.5 rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                                  <ChevronUp className="w-3.5 h-3.5" />
                                </button>
                                <button type="button" disabled={idx === editExtraStops.length - 1}
                                  onClick={() => {
                                    const updated = [...editExtraStops]
                                    ;[updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]]
                                    setEditExtraStops(updated)
                                  }}
                                  className="p-1.5 rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                                  <ChevronDown className="w-3.5 h-3.5" />
                                </button>
                                <button type="button" onClick={() => setEditExtraStops(editExtraStops.filter((_, i) => i !== idx))}
                                  className="p-1.5 rounded-md bg-red-50 text-red-400 hover:bg-red-100 transition-colors">
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          ))}
                          <button type="button" onClick={() => setEditExtraStops([...editExtraStops, { address: '', name: '', phone: '' }])}
                            className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-dashed border-blue-300 text-blue-500 text-sm hover:bg-blue-50 transition-colors">
                            <Plus className="w-4 h-4" />新增附加乘客
                          </button>

                          <div className="grid grid-cols-2 gap-2">
                            <ContactAutocomplete
                              token={token || ''}
                pin={pinVerified || undefined}
                onDeleted={(c: Contact) => showToast(`已刪除「${c.name}」`)}
                              value={editForm.passenger_name}
                              onChange={v => setEditForm(f => ({ ...f, passenger_name: v }))}
                              onSelect={(c: Contact) => setEditForm(f => ({ ...f, passenger_name: c.name, passenger_phone: c.phone }))}
                              placeholder="乘客姓名"
                            />
                            <ContactAutocomplete
                              token={token || ''}
                pin={pinVerified || undefined}
                onDeleted={(c: Contact) => showToast(`已刪除「${c.name}」`)}
                              type="tel"
                              value={editForm.passenger_phone}
                              onChange={v => setEditForm(f => ({ ...f, passenger_phone: v }))}
                              onSelect={(c: Contact) => setEditForm(f => ({ ...f, passenger_name: c.name, passenger_phone: c.phone }))}
                              placeholder="聯絡電話"
                            />
                          </div>
                          {editForm.passenger_name.trim() && editForm.passenger_phone.trim() && (
                            <button type="button"
                              onClick={() => saveContact(editForm.passenger_name, editForm.passenger_phone, editForm.pickup_location)}
                              className="text-xs text-blue-600 hover:text-blue-700 underline">
                              + 加入常用聯絡人
                            </button>
                          )}
                          <div className="grid grid-cols-2 gap-2">
                            <input className={inputCls()} type="number" min={1} placeholder="人數" value={editForm.passenger_count}
                              onChange={e => setEditForm(f => ({ ...f, passenger_count: Number(e.target.value) }))} />
                            <input className={inputCls()} placeholder="航班編號（選填）" value={editForm.flight_number}
                              onChange={e => setEditForm(f => ({ ...f, flight_number: e.target.value }))} />
                          </div>
                          <textarea className={inputCls()} rows={2} placeholder="備註（選填）" value={editForm.driver_notes}
                            onChange={e => setEditForm(f => ({ ...f, driver_notes: e.target.value }))} />
                          {editError && <p className="text-red-500 text-xs">{editError}</p>}
                          <div className="flex gap-2">
                            <button type="button" onClick={() => setEditingOrder(null)}
                              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium py-2 rounded-lg transition-colors">
                              取消
                            </button>
                            <button type="button" disabled={editSubmitting} onClick={() => submitEditOrder(order.order_number)}
                              className="flex-1 bg-[#2563EB] hover:bg-blue-700 text-white text-sm font-medium py-2 rounded-lg transition-colors disabled:opacity-60">
                              {editSubmitting ? '儲存中...' : '儲存修改'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* 價目表 */}
        {tab === 'prices' && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
              <ReceiptText className="w-5 h-5 text-blue-500" />
              <h2 className="font-bold text-gray-800">合約價目表</h2>
              <span className="text-xs text-gray-400 ml-1">（僅供參考，實際費用以業務確認為準）</span>
            </div>
            {priceRules.length === 0 ? (
              <div className="p-10 text-center text-gray-400 text-sm">尚無合約定價資料</div>
            ) : (
              <div className="divide-y divide-gray-50">
                {/* 表頭 */}
                <div className="grid grid-cols-12 gap-2 px-5 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <div className="col-span-5">出發地</div>
                  <div className="col-span-5">目的地</div>
                  <div className="col-span-2 text-right">金額</div>
                </div>
                {priceRules.map(rule => (
                  <div key={rule.id} className="grid grid-cols-12 gap-2 px-5 py-3.5 items-center hover:bg-blue-50/30 transition-colors">
                    <div className="col-span-5">
                      <p className="text-sm font-medium text-gray-800">{rule.from_area}</p>
                      {rule.vehicle_type && (
                        <span className="text-xs text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded mt-0.5 inline-block">{rule.vehicle_type}</span>
                      )}
                    </div>
                    <div className="col-span-5 flex items-center gap-1.5">
                      <ArrowRight className="w-3 h-3 text-gray-300 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-800">{rule.to_area}</p>
                        {rule.is_bidirectional && (
                          <span className="text-xs text-gray-400">雙向</span>
                        )}
                      </div>
                    </div>
                    <div className="col-span-2 text-right">
                      <p className="text-sm font-bold text-blue-600">NT${rule.price.toLocaleString()}</p>
                      {rule.note && <p className="text-xs text-gray-400 mt-0.5">{rule.note}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 常用聯絡人 */}
        {tab === 'contacts' && !pinVerified && (
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center space-y-4">
            <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mx-auto">
              <Phone className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="font-medium text-gray-800 mb-1">請輸入驗證碼</p>
              <p className="text-xs text-gray-400">預設為您的手機末 4 碼，或管理員設定的驗證碼</p>
            </div>
            <div className="max-w-xs mx-auto space-y-3">
              <input
                type="tel"
                maxLength={10}
                value={pinInput}
                onChange={e => { setPinInput(e.target.value); setPinError('') }}
                onKeyDown={e => e.key === 'Enter' && handleVerifyPin()}
                placeholder="輸入驗證碼"
                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-center text-lg tracking-widest outline-none focus:ring-2 focus:ring-blue-300"
                autoFocus
              />
              {pinError && <p className="text-red-500 text-sm">{pinError}</p>}
              <button
                type="button"
                onClick={handleVerifyPin}
                disabled={pinLoading}
                className="w-full bg-[#2563EB] hover:bg-blue-700 text-white font-medium py-2.5 rounded-xl transition-colors disabled:opacity-60"
              >
                {pinLoading ? '驗證中...' : '確認驗證'}
              </button>
            </div>
          </div>
        )}

        {tab === 'contacts' && pinVerified && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-500" />
              <h2 className="font-bold text-gray-800">常用聯絡人</h2>
            </div>
            {contactsLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : contactsList.length === 0 ? (
              <div className="p-10 text-center text-gray-400 text-sm">尚無常用聯絡人</div>
            ) : (
              <div className="divide-y divide-gray-50">
                {contactsList.map(c => (
                  <div key={c.id} className="flex items-center justify-between gap-3 px-5 py-3.5">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800">{c.name}　{c.phone}</p>
                      {c.address && <p className="text-xs text-gray-500 mt-0.5 truncate">{c.address}</p>}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteContact(c)}
                      className="shrink-0 p-1.5 rounded-md bg-red-50 text-red-400 hover:bg-red-100 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 預約表單 */}
        {tab === 'booking' && submitted ? (
          <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
            <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#1E40AF] mb-2">預約成功</h2>
            <p className="text-gray-600 mb-4">我們已收到您的預約申請，將盡快與您確認行程。</p>
            <div className="bg-blue-50 rounded-lg px-4 py-3 text-sm text-blue-700 mb-4">
              訂單編號：<span className="font-mono font-semibold">{orderNumber}</span>
            </div>
            <button
              type="button"
              onClick={() => { setSubmitted(false); setTab('orders') }}
              className="text-sm text-blue-600 underline"
            >
              查看我的訂單
            </button>
          </div>
        ) : tab === 'booking' && (

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-6 space-y-5">

          {/* 行程類別 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">行程類別</label>
            <div className="flex gap-2">
              {ORDER_CATEGORIES.map(cat => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => {
                    set('order_category', cat)
                    set('pickup_location', '')
                    set('dropoff_location', '')
                    setExtraStops([])
                  }}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                    form.order_category === cat
                      ? 'bg-[#2563EB] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* 合約定價區域選擇（有合約定價規則時顯示） */}
          {priceRules.length > 0 && form.order_category === '交通趟' && (
            <div className="bg-blue-50 rounded-xl p-4 space-y-3">
              <p className="text-sm font-medium text-blue-700 flex items-center gap-1.5">
                <Tag className="w-4 h-4" />合約定價
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">出發地區</label>
                  <SearchableSelect
                    options={uniqueAreas}
                    value={fromArea}
                    onChange={setFromArea}
                    placeholder="— 選擇 —"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">目的地區</label>
                  <SearchableSelect
                    options={uniqueAreas}
                    value={toArea}
                    onChange={setToArea}
                    placeholder="— 選擇 —"
                  />
                </div>
              </div>
              {fromArea && toArea && (
                matchedRule ? (
                  <div className="flex items-center justify-between bg-white rounded-lg px-4 py-2.5 border border-blue-200">
                    <span className="text-sm text-gray-600">{matchedRule.from_area} → {matchedRule.to_area}</span>
                    <span className="text-lg font-bold text-blue-700">NT$ {matchedRule.price.toLocaleString()}</span>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 text-center py-1">此路線無合約定價，請洽業務確認費用</p>
                )
              )}
            </div>
          )}

          {/* 上車日期 | 時間 */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Calendar className="w-4 h-4 inline mr-1" />上車日期 <span className="text-red-500">*</span>
              </label>
              <MonthDayPicker
                value={form.pickup_date}
                onChange={v => set('pickup_date', v)}
                placeholder="選擇日期"
              />
              {errors.pickup_date && <p className="text-red-500 text-xs mt-1">{errors.pickup_date}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Clock className="w-4 h-4 inline mr-1" />上車時間 <span className="text-red-500">*</span>
              </label>
              <TimePicker
                value={form.pickup_time}
                onChange={v => set('pickup_time', v)}
                placeholder="選擇時間"
              />
              {errors.pickup_time && <p className="text-red-500 text-xs mt-1">{errors.pickup_time}</p>}
            </div>
          </div>

          {/* 乘客姓名 | 聯絡電話 */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <User className="w-4 h-4 inline mr-1" />乘客姓名 <span className="text-red-500">*</span>
              </label>
              <ContactAutocomplete
                token={token || ''}
                pin={pinVerified || undefined}
                onDeleted={(c: Contact) => showToast(`已刪除「${c.name}」`)}
                value={form.passenger_name}
                onChange={v => set('passenger_name', v)}
                onSelect={(c: Contact) => { set('passenger_name', c.name); set('passenger_phone', c.phone) }}
                placeholder="乘客姓名"
                className={inputCls(!!errors.passenger_name)}
              />
              {errors.passenger_name && <p className="text-red-500 text-xs mt-1">{errors.passenger_name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Phone className="w-4 h-4 inline mr-1" />聯絡電話 <span className="text-red-500">*</span>
              </label>
              <ContactAutocomplete
                token={token || ''}
                pin={pinVerified || undefined}
                onDeleted={(c: Contact) => showToast(`已刪除「${c.name}」`)}
                type="tel"
                value={form.passenger_phone}
                onChange={v => set('passenger_phone', v)}
                onSelect={(c: Contact) => { set('passenger_name', c.name); set('passenger_phone', c.phone) }}
                placeholder="0912-345-678"
                className={inputCls(!!errors.passenger_phone)}
              />
              {errors.passenger_phone && <p className="text-red-500 text-xs mt-1">{errors.passenger_phone}</p>}
            </div>
          </div>
          {form.passenger_name.trim() && form.passenger_phone.trim() && (
            <button
              type="button"
              onClick={() => saveContact(form.passenger_name, form.passenger_phone, form.pickup_location)}
              className="text-xs text-blue-600 hover:text-blue-700 underline"
            >
              + 加入常用聯絡人
            </button>
          )}

          {/* 上車地點 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <MapPin className="w-4 h-4 inline mr-1" />上車地點 <span className="text-red-500">*</span>
            </label>
            {pickupIsAirport ? (
              <select
                value={form.pickup_location}
                onChange={e => set('pickup_location', e.target.value)}
                className={inputCls(!!errors.pickup_location)}
              >
                <option value="">選擇機場</option>
                {AIRPORTS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            ) : (
              <AddressAutocomplete
                value={form.pickup_location}
                onChange={v => set('pickup_location', v)}
                placeholder="請輸入上車地址"
                className={errors.pickup_location ? 'border-red-400' : ''}
              />
            )}
            {errors.pickup_location && <p className="text-red-500 text-xs mt-1">{errors.pickup_location}</p>}
          </div>

          {/* 附加乘客（中途停靠點，可各自填姓名/電話，選填） */}
          {extraStops.map((stop, idx) => (
            <div key={idx} className="flex gap-2 items-start bg-gray-50 rounded-xl p-3">
              <div className="flex-1 space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  <MapPin className="w-4 h-4 inline mr-1" />附加乘客 {extraStops.length > 1 ? idx + 1 : ''}
                </label>
                <AddressAutocomplete
                  value={stop.address}
                  onChange={v => {
                    const updated = [...extraStops]
                    updated[idx] = { ...updated[idx], address: v }
                    setExtraStops(updated)
                  }}
                  placeholder="請輸入地址"
                />
                <div className="grid grid-cols-2 gap-2">
                  <ContactAutocomplete
                    token={token || ''}
                pin={pinVerified || undefined}
                onDeleted={(c: Contact) => showToast(`已刪除「${c.name}」`)}
                    value={stop.name}
                    onChange={v => {
                      const updated = [...extraStops]
                      updated[idx] = { ...updated[idx], name: v }
                      setExtraStops(updated)
                    }}
                    onSelect={(c: Contact) => {
                      const updated = [...extraStops]
                      updated[idx] = { ...updated[idx], name: c.name, phone: c.phone, address: c.address || updated[idx].address }
                      setExtraStops(updated)
                    }}
                    placeholder="姓名（選填）"
                  />
                  <ContactAutocomplete
                    token={token || ''}
                pin={pinVerified || undefined}
                onDeleted={(c: Contact) => showToast(`已刪除「${c.name}」`)}
                    type="tel"
                    value={stop.phone}
                    onChange={v => {
                      const updated = [...extraStops]
                      updated[idx] = { ...updated[idx], phone: v }
                      setExtraStops(updated)
                    }}
                    onSelect={(c: Contact) => {
                      const updated = [...extraStops]
                      updated[idx] = { ...updated[idx], name: c.name, phone: c.phone, address: c.address || updated[idx].address }
                      setExtraStops(updated)
                    }}
                    placeholder="電話（選填）"
                  />
                </div>
                {stop.name.trim() && stop.phone.trim() && (
                  <button
                    type="button"
                    onClick={() => saveContact(stop.name, stop.phone, stop.address)}
                    className="text-xs text-blue-600 hover:text-blue-700 underline"
                  >
                    + 加入常用聯絡人
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-1 mt-6 shrink-0">
                <button
                  type="button"
                  disabled={idx === 0}
                  onClick={() => {
                    const updated = [...extraStops]
                    ;[updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]]
                    setExtraStops(updated)
                  }}
                  className="p-1.5 rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  disabled={idx === extraStops.length - 1}
                  onClick={() => {
                    const updated = [...extraStops]
                    ;[updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]]
                    setExtraStops(updated)
                  }}
                  className="p-1.5 rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setExtraStops(extraStops.filter((_, i) => i !== idx))}
                  className="p-1.5 rounded-md bg-red-50 text-red-400 hover:bg-red-100 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={() => setExtraStops([...extraStops, { address: '', name: '', phone: '' }])}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-blue-300 text-blue-500 text-sm hover:bg-blue-50 transition-colors"
          >
            <Plus className="w-4 h-4" />新增附加乘客
          </button>

          {/* 下車地點 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <MapPin className="w-4 h-4 inline mr-1" />下車地點 <span className="text-red-500">*</span>
            </label>
            {dropoffIsAirport ? (
              <select
                value={form.dropoff_location}
                onChange={e => set('dropoff_location', e.target.value)}
                className={inputCls(!!errors.dropoff_location)}
              >
                <option value="">選擇機場</option>
                {AIRPORTS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            ) : (
              <AddressAutocomplete
                value={form.dropoff_location}
                onChange={v => set('dropoff_location', v)}
                placeholder="請輸入下車地址"
                className={errors.dropoff_location ? 'border-red-400' : ''}
              />
            )}
            {errors.dropoff_location && <p className="text-red-500 text-xs mt-1">{errors.dropoff_location}</p>}
          </div>

          {/* 車型 | 人數 | 行李大 | 行李小 */}
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Car className="w-4 h-4 inline mr-1" />車型 <span className="text-red-500">*</span>
              </label>
              <select
                value={form.vehicle_type}
                onChange={e => set('vehicle_type', e.target.value)}
                className={inputCls(!!errors.vehicle_type)}
              >
                {info.vehicle_categories.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
              {errors.vehicle_type && <p className="text-red-500 text-xs mt-1">{errors.vehicle_type}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <User className="w-4 h-4 inline mr-1" />人數
              </label>
              <input
                type="number" min={1} max={20}
                value={form.passenger_count}
                onChange={e => set('passenger_count', e.target.value)}
                className={inputCls()}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Luggage className="w-4 h-4 inline mr-1" />行李大
              </label>
              <input
                type="number" min={0} max={20}
                value={form.luggage_large}
                onChange={e => set('luggage_large', e.target.value)}
                className={inputCls()}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Luggage className="w-4 h-4 inline mr-1" />行李小
              </label>
              <input
                type="number" min={0} max={20}
                value={form.luggage_small}
                onChange={e => set('luggage_small', e.target.value)}
                className={inputCls()}
              />
            </div>
          </div>

          {/* 航班資訊（送機/接機） */}
          {isAirport && (
            <div className="bg-blue-50 rounded-xl p-4 space-y-3">
              <p className="text-sm font-medium text-blue-700">航班資訊（選填）</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">航班編號</label>
                  <input
                    type="text"
                    value={form.flight_number}
                    onChange={e => set('flight_number', e.target.value)}
                    placeholder="例：CI100"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-300"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    {form.order_category === '接機' ? '抵達時間' : '起飛時間'}
                  </label>
                  <TimePicker
                    value={form.flight_time}
                    onChange={v => set('flight_time', v)}
                    placeholder="選擇時間"
                  />
                </div>
              </div>
            </div>
          )}

          {/* 備註 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <FileText className="w-4 h-4 inline mr-1" />備註
            </label>
            <textarea
              value={form.driver_notes}
              onChange={e => set('driver_notes', e.target.value)}
              placeholder="其他需求或注意事項..."
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-300 resize-none"
            />
          </div>

          {errors.submit && (
            <p className="text-red-500 text-sm text-center">{errors.submit}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#F97316] hover:bg-orange-600 text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-60"
          >
            {submitting ? '送出中...' : '送出預約'}
          </button>
        </form>
        )}

        <p className="text-center text-xs text-gray-400 mt-4">
          送出後我們將與您確認行程細節
        </p>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-full shadow-lg z-[200]">
          {toast}
        </div>
      )}
    </div>
  )
}
